import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { LoadingSpinner } from '../ui';

interface Column<T> {
  key: keyof T | string;
  title: string;
  sortable?: boolean;
  render?: (value: any, item: T) => React.ReactNode;
  width?: string;
}

interface AdminTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  actions?: (item: T) => React.ReactNode;
  emptyMessage?: string;
}

function AdminTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  actions,
  emptyMessage = 'No data available'
}: AdminTableProps<T>) {
  if (loading) {
    return (
      <div className="sf-card">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className="sf-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="border-b border-white/[.08] bg-white/[.02]">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`
                    px-4 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta
                    ${column.sortable && onSort ? 'cursor-pointer transition-colors hover:bg-white/[.04] hover:text-fg-soft' : ''}
                    ${column.width ? column.width : ''}
                  `}
                  onClick={() => column.sortable && onSort && onSort(String(column.key))}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.title}</span>
                    {column.sortable && onSort && (
                      <div className="flex flex-col">
                        <ChevronUp
                          className={`h-3 w-3 ${
                            sortBy === column.key && sortOrder === 'asc'
                              ? 'text-neon-cyan'
                              : 'text-fg-faint'
                          }`}
                        />
                        <ChevronDown
                          className={`h-3 w-3 -mt-1 ${
                            sortBy === column.key && sortOrder === 'desc'
                              ? 'text-neon-cyan'
                              : 'text-fg-faint'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3 text-right font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                  Acties
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="px-6 py-12 text-center italic text-fg-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={index}
                  className="border-b border-white/[.06] last:border-b-0 transition-colors hover:bg-white/[.03]"
                >
                  {columns.map((column) => (
                    <td key={String(column.key)} className="px-4 py-3 whitespace-nowrap text-sm text-fg-body">
                      {column.render
                        ? column.render(item[column.key], item)
                        : String(item[column.key] || '-')}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      {actions(item)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminTable;
